import React, { useState } from 'react';
import { Box, Card, Typography, Button, Badge, Chip, Paper, IconButton, Select, MenuItem } from '@mui/material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WarningIcon from '@mui/icons-material/Warning';
import LanguageIcon from '@mui/icons-material/Language';
import { useApp } from '../context/AppContext';

export default function QROrderingCustomerView() {
    const { menuItems } = useApp();
    const [cart, setCart] = useState({});
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [lang, setLang] = useState('mr'); // Default to Marathi for local demo feel

    const params = new URLSearchParams(window.location.search);
    const tableNo = params.get('table') || '04';

    const handleAddToCart = (id) => {
        setCart(prev => ({
            ...prev,
            [id]: (prev[id] || 0) + 1
        }));
    };

    const handleRemoveFromCart = (id) => {
        setCart(prev => {
            const copy = { ...prev };
            if (copy[id] <= 1) {
                delete copy[id];
            } else {
                copy[id] -= 1;
            }
            return copy;
        });
    };

    const handlePlaceOrder = async () => {
        const cartTotal = Object.entries(cart).reduce((sum, [id, qty]) => {
            const item = menuItems.find(m => m.id === parseInt(id));
            return sum + (item ? item.price * qty : 0);
        }, 0);

        const newOrder = {
            id: 'QRO-' + Math.floor(1000 + Math.random() * 9000),
            table: tableNo,
            items: Object.entries(cart).map(([id, qty]) => {
                const menuItem = menuItems.find(m => m.id === parseInt(id));
                return {
                    name: menuItem.name,
                    price: menuItem.price,
                    quantity: qty
                };
            }),
            total: cartTotal,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'Pending'
        };

        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        if (isLocal) {
            const existing = JSON.parse(localStorage.getItem('qr_orders') || '[]');
            existing.push(newOrder);
            localStorage.setItem('qr_orders', JSON.stringify(existing));
            window.dispatchEvent(new Event('storage'));
        } else {
            try {
                await fetch('./sync.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newOrder)
                });
            } catch (err) {
                console.error("Failed to sync order to server", err);
            }
        }

        setOrderSuccess(true);
        setCart({});
    };

    const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
    const cartTotal = Object.entries(cart).reduce((sum, [id, qty]) => {
        const item = menuItems.find(m => m.id === parseInt(id));
        return sum + (item ? item.price * qty : 0);
    }, 0);

    const getMockCalories = (item) => {
        if (item.category === 'desserts') return '310 kcal';
        if (item.category === 'beverages') return '120 kcal';
        if (item.category === 'breads') return '150 kcal';
        return item.veg ? '260 kcal' : '420 kcal';
    };

    const getMockAllergens = (item) => {
        const list = [];
        if (item.category === 'desserts' || item.category === 'breads') list.push('Gluten');
        if (item.category === 'desserts' || item.name.toLowerCase().includes('paneer') || item.name.toLowerCase().includes('butter')) {
            list.push('Dairy');
        }
        return list;
    };

    const getItemName = (item) => {
        if (lang === 'mr' && item.nameMr) return item.nameMr;
        if (lang === 'hi' && item.nameHi) return item.nameHi;
        return item.name;
    };

    const labels = {
        en: {
            title: 'Dinexa Digital Menu',
            table: 'Table No',
            orderPlaced: 'Order Placed!',
            kotSuccess: 'Your KOT has been routed directly to the Kitchen KDS station.',
            subtotal: 'Subtotal',
            placeOrder: 'Place Order',
            add: 'ADD'
        },
        mr: {
            title: 'डायनेक्सा डिजिटल मेनू',
            table: 'टेबल नंबर',
            orderPlaced: 'ऑर्डर यशस्वी!',
            kotSuccess: 'तुमची ऑर्डर किचन मध्ये पाठवण्यात आली आहे. लवकरच वाढली जाईल.',
            subtotal: 'एकूण',
            placeOrder: 'ऑर्डर पाठवा',
            add: 'ADD'
        },
        hi: {
            title: 'डाइनेक्सा डिजिटल मेनू',
            table: 'टेबल नंबर',
            orderPlaced: 'ऑर्डर सफल!',
            kotSuccess: 'आपकी ऑर्डर रसोई में भेज दी गई है। जल्द ही परोसी जाएगी।',
            subtotal: 'कुल योग',
            placeOrder: 'ऑर्डर भेजें',
            add: 'ADD'
        }
    };

    const t = labels[lang];

    return (
        <Box sx={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', pb: 10 }}>
            {/* Header Area */}
            <Paper square sx={{ bgcolor: '#008751', color: '#fff', px: 2, pt: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
                        {t.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 800 }}>
                        {t.table}: {tableNo}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Language Switcher */}
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1.5, px: 1 }}>
                        <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Select
                            value={lang}
                            onChange={(e) => setLang(e.target.value)}
                            variant="standard"
                            disableUnderline
                            sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 800, py: 0.5 }}
                        >
                            <MenuItem value="en">EN</MenuItem>
                            <MenuItem value="mr">मराठी</MenuItem>
                            <MenuItem value="hi">हिंदी</MenuItem>
                        </Select>
                    </Box>
                    <Badge badgeContent={cartCount} color="error">
                        <ShoppingBagIcon />
                    </Badge>
                </Box>
            </Paper>

            {/* Menu Body */}
            <Box sx={{ p: 2, flexGrow: 1 }}>
                {orderSuccess ? (
                    <Card sx={{ p: 4, textAlign: 'center', borderRadius: 4, mt: 5, boxShadow: '0 8px 30px rgba(0,0,0,0.05)' }}>
                        <Typography variant="h4" sx={{ mb: 2 }}>🎉</Typography>
                        <Typography variant="h5" sx={{ color: '#008751', fontWeight: 900, mb: 1.5 }}>
                            {t.orderPlaced}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                            {t.kotSuccess}
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={() => setOrderSuccess(false)}
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                        >
                            Order More / पुन्हा मागवा
                        </Button>
                    </Card>
                ) : (
                    <Box>
                        {menuItems.map((item) => {
                            const qty = cart[item.id] || 0;
                            return (
                                <Card key={item.id} sx={{ mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid #e2e8f0', display: 'flex', p: 1.5, position: 'relative' }}>
                                    {/* Veg/Non-Veg Dot */}
                                    <Box sx={{
                                        width: 14, height: 14, 
                                        border: `2px solid ${item.veg ? '#10b981' : '#ef4444'}`,
                                        p: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        position: 'absolute', top: 12, left: 12, zIndex: 2
                                    }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: item.veg ? '#10b981' : '#ef4444' }} />
                                    </Box>

                                    <Box sx={{ flexGrow: 1, pl: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                            {getItemName(item)}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ color: '#008751', fontWeight: 800, my: 0.5 }}>
                                            ₹{item.price}
                                        </Typography>

                                        {/* Calorie/Allergen Badge */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            <Chip 
                                                size="small" 
                                                label={getMockCalories(item)} 
                                                icon={<LocalFireDepartmentIcon sx={{ fontSize: '12px !important', color: '#f97316 !important' }} />}
                                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}
                                            />
                                            {getMockAllergens(item).map(a => (
                                                <Chip 
                                                    key={a} 
                                                    size="small" 
                                                    label={a} 
                                                    icon={<WarningIcon sx={{ fontSize: '11px !important' }} />}
                                                    sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#991b1b' }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Add Control */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 80 }}>
                                        {qty > 0 ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#008751', borderRadius: 2, color: '#fff', px: 0.5 }}>
                                                <IconButton size="small" onClick={() => handleRemoveFromCart(item.id)} sx={{ color: '#fff' }}>
                                                    <RemoveIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                                <Typography variant="body2" sx={{ mx: 1, fontWeight: 800 }}>{qty}</Typography>
                                                <IconButton size="small" onClick={() => handleAddToCart(item.id)} sx={{ color: '#fff' }}>
                                                    <AddIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                onClick={() => handleAddToCart(item.id)}
                                                sx={{ fontWeight: 800, borderColor: '#008751', color: '#008751', borderRadius: 2, px: 2 }}
                                            >
                                                {t.add}
                                            </Button>
                                        )}
                                    </Box>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Sticky Bottom Cart Settle */}
            {cartTotal > 0 && (
                <Paper square sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#fff', zIndex: 100, maxWidth: 480, margin: '0 auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{t.subtotal}</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#008751' }}>₹{cartTotal}</Typography>
                    </Box>
                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={handlePlaceOrder}
                        sx={{ bgcolor: '#008751', fontWeight: 800, py: 1.2, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        {t.placeOrder} (₹{cartTotal})
                    </Button>
                </Paper>
            )}
        </Box>
    );
}
